<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.addPackage") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #ef3405">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <div v-if="formErrors.length" class="package_form_errors">
        <div class="package_form_errors__header">
          <i class="fal fa-exclamation-triangle"></i>
          <span>{{ $t("MESSAGES.fixFormErrors") }}</span>
        </div>
        <ul>
          <li v-for="(error, index) in formErrors" :key="`error-${index}`">
            {{ error }}
          </li>
        </ul>
      </div>

      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <div class="col-12">
            <h5 class="package_form_section">
              {{ $t("PLACEHOLDERS.customer_info") }}
            </h5>
          </div>

          <!-- Start:: User Name Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.name')"
            v-model.trim="data.name"
            required
          />
          <!-- End:: User Name Input -->

          <!-- Start:: Mobile Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.mobile"
            required
          />
          <!-- End:: Mobile Input -->

          <!-- Start:: City Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.city')"
            v-model.trim="data.city"
            required
          />
          <!-- End:: City Input -->

          <!-- Start:: Zone Input -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.zone')"
            v-model.trim="data.zone"
            required
          />
          <!-- End:: Zone Input -->

          <!-- Start:: Address Input -->
          <base-input
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.address')"
            v-model.trim="data.address"
            required
          />
          <!-- End:: Address Input -->

          <div class="col-12">
            <h5 class="package_form_section">
              {{ $t("PLACEHOLDERS.package_dimensions") }}
            </h5>
          </div>

          <!-- Start:: Weight Input -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.weight_kg')"
            v-model.trim="data.weight"
            required
          />
          <!-- End:: Weight Input -->

          <!-- Start:: Width Input -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.width_cm')"
            v-model.trim="data.width"
            required
          />
          <!-- End:: Width Input -->

          <!-- Start:: Height Input -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.height_cm')"
            v-model.trim="data.height"
            required
          />
          <!-- End:: Height Input -->

          <!-- Start:: Length Input -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.length_cm')"
            v-model.trim="data.length"
            required
          />
          <!-- End:: Length Input -->

          <div class="col-12">
            <h5 class="package_form_section">
              {{ $t("PLACEHOLDERS.additional_info") }}
            </h5>
          </div>

          <!-- Start:: Notes Input -->
          <base-input
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.notes_optional')"
            v-model.trim="data.notes"
          />
          <!-- End:: Notes Input -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
export default {
  name: "CreatePackage",

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      formErrors: [],
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        name: null,
        mobile: null,
        city: null,
        zone: null,
        address: null,
        weight: null,
        width: null,
        height: null,
        length: null,
        notes: null,
      },
      // End:: Data Collection To Send
    };
  },

  methods: {
    showValidationError(message) {
      this.formErrors = [message];
      this.$message.error(message);
      this.scrollToErrors();
    },

    scrollToErrors() {
      this.$nextTick(() => {
        this.$el
          .querySelector(".package_form_errors")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },

    isValidMobile(mobile) {
      const digits = String(mobile).replace(/\D/g, "");
      return digits.length >= 9 && digits.length <= 15;
    },

    validatePositiveNumber(value, fieldLabel) {
      if (value === null || value === "" || value === undefined) {
        return this.$t("VALIDATION.requiredField", { field: fieldLabel });
      }

      const numberValue = Number(value);
      if (Number.isNaN(numberValue) || numberValue <= 0) {
        return this.$t("VALIDATION.positiveField", { field: fieldLabel });
      }

      return null;
    },

    // Start:: validate Form Inputs
    validateFormInputs() {
      this.formErrors = [];

      if (!this.data.name || !String(this.data.name).trim()) {
        this.showValidationError(this.$t("VALIDATION.name"));
        return;
      }

      if (!this.data.mobile || !String(this.data.mobile).trim()) {
        this.showValidationError(this.$t("MESSAGES.phoneRequired"));
        return;
      }

      if (!this.isValidMobile(this.data.mobile)) {
        this.showValidationError(this.$t("VALIDATION.invalidPhone"));
        return;
      }

      if (!this.data.address || !String(this.data.address).trim()) {
        this.showValidationError(this.$t("VALIDATION.address"));
        return;
      }

      const dimensionChecks = [
        { value: this.data.width, label: this.$t("PLACEHOLDERS.width_cm") },
        { value: this.data.height, label: this.$t("PLACEHOLDERS.height_cm") },
        { value: this.data.length, label: this.$t("PLACEHOLDERS.length_cm") },
      ];

      for (const dimension of dimensionChecks) {
        const error = this.validatePositiveNumber(
          dimension.value,
          dimension.label
        );
        if (error) {
          this.showValidationError(error);
          return;
        }
      }

      if (
        this.data.weight !== null &&
        this.data.weight !== "" &&
        this.data.weight !== undefined
      ) {
        const weightError = this.validatePositiveNumber(
          this.data.weight,
          this.$t("PLACEHOLDERS.weight_kg")
        );
        if (weightError) {
          this.showValidationError(weightError);
          return;
        }
      }

      this.isWaitingRequest = true;
      this.submitForm();
    },
    // End:: validate Form Inputs

    getFieldLabel(field) {
      const labels = {
        name: this.$t("PLACEHOLDERS.name"),
        mobile: this.$t("PLACEHOLDERS.phone"),
        city: this.$t("PLACEHOLDERS.city"),
        zone: this.$t("PLACEHOLDERS.zone"),
        address: this.$t("PLACEHOLDERS.address"),
        weight: this.$t("PLACEHOLDERS.weight_kg"),
        width: this.$t("PLACEHOLDERS.width_cm"),
        height: this.$t("PLACEHOLDERS.height_cm"),
        length: this.$t("PLACEHOLDERS.length_cm"),
        notes: this.$t("PLACEHOLDERS.notes_optional"),
      };

      return labels[field] || field;
    },

    handleSubmitError(error) {
      this.formErrors = [];

      if (!error.response) {
        const networkError = this.$t("MESSAGES.networkError");
        this.formErrors = [networkError];
        this.$message.error(networkError);
        this.scrollToErrors();
        return;
      }

      const errorData = error.response.data || {};

      if (errorData.errors && typeof errorData.errors === "object") {
        Object.keys(errorData.errors).forEach((field) => {
          const messages = Array.isArray(errorData.errors[field])
            ? errorData.errors[field]
            : [errorData.errors[field]];

          messages.filter(Boolean).forEach((message) => {
            this.formErrors.push(`${this.getFieldLabel(field)}: ${message}`);
          });
        });
      }

      if (!this.formErrors.length && errorData.message) {
        this.formErrors = [errorData.message];
      }

      if (!this.formErrors.length) {
        this.formErrors = [this.$t("MESSAGES.failed")];
      }

      this.$message.error(this.formErrors[0]);
      this.scrollToErrors();
    },

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();
      // Start:: Append Request Data
      if (this.data.name) {
        REQUEST_DATA.append("name", this.data.name);
      }
      if (this.data.mobile) {
        REQUEST_DATA.append("mobile", this.data.mobile);
      }
      if (this.data.city) {
        REQUEST_DATA.append("city", this.data.city);
      }
      if (this.data.zone) {
        REQUEST_DATA.append("zone", this.data.zone);
      }
      if (this.data.address) {
        REQUEST_DATA.append("address", this.data.address);
      }
      if (this.data.weight) {
        REQUEST_DATA.append("weight", this.data.weight);
      }
      if (this.data.width) {
        REQUEST_DATA.append("width", this.data.width);
      }
      if (this.data.height) {
        REQUEST_DATA.append("height", this.data.height);
      }
      if (this.data.length) {
        REQUEST_DATA.append("length", this.data.length);
      }
      if (this.data.notes) {
        REQUEST_DATA.append("notes", this.data.notes);
      }
      // End:: Append Request Data

      try {
        await this.$axios({
          method: "POST",
          url: `packages`,
          data: REQUEST_DATA,
        });
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        this.$router.push({ path: "/packages/all" });
      } catch (error) {
        this.handleSubmitError(error);
      } finally {
        this.isWaitingRequest = false;
      }
    },
    // End:: Submit Form
  },
};
</script>

<style lang="scss" scoped>
.package_form_section {
  margin: 0.5rem 0 0.25rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid var(--border_clr);
  color: var(--main_theme_clr);
  font-size: 1rem;
  font-weight: 700;
}

.package_form_errors {
  margin-bottom: 1.25rem;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  border: 1px solid rgba(220, 53, 69, 0.25);
  background: rgba(220, 53, 69, 0.08);

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.65rem;
    color: var(--mid_red_clr);
    font-weight: 700;
  }

  ul {
    margin: 0;
    padding-inline-start: 1.2rem;
    color: var(--theme_text_clr);

    li + li {
      margin-top: 0.35rem;
    }
  }
}
</style>
